/**
 * 🦊 **Drawing the foxes** — T288, decision 134.
 *
 * 🔴 **The sheep's machinery, pointed at a second animal** (`sheepmesh.ts`):
 * one rigged body loaded once, MEASURED, its anatomy DERIVED from its own
 * skeleton (`quadrig.ts`), cloned per fox with `SkeletonUtils.clone`, posed
 * every frame. 📷 The body is haugur's pick of four — *"3 is fine by me"* —
 * `Design/samples/164_fox_candidates.png`.
 *
 * | What the server says it is doing | What is drawn |
 * |---|---|
 * | `Sleep` | curled up, nose under its tail |
 * | `Watch` | sitting on its haunches, staring at you |
 * | `Flee`, `Carry`, `Stalk`, `Wander` | the walk, faster as it goes faster |
 * | `Sniff` | a slow walk, nose to the ground |
 * | `Pounce` | the mousing leap, nose-first into the grass |
 * | `Dead` | on its side, as a sheep falls |
 *
 * 🎨 **Two coats** (`foxCoat`): two in three dark, one in three white — and a
 * white fox is brown-grey in summer and white November to April. **The body is
 * generated white**, so every coat is a MULTIPLY on one material path, built
 * when the model loads and swapped, never edited mid-game (the blackout trap).
 */

import { Box3, Color, Group, type Object3D, Vector3 } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { uniform } from 'three/tsl';
import type { Bulk, CreaturesMessage } from '@lofi/shared';
import {
  FOX_HEIGHT,
  FOX_KIND,
  FOX_LENGTH,
  FOX_SPEED,
  FOX_WIDTH,
  foxCoat,
  isWinterCoat,
} from '@lofi/shared';
import { HURT_BARE, HURT_FLEECE, creatureMaterial, markFleece } from './creaturepaint.ts';
import { shedRootWeights } from './rootweights.ts';
import { headTowards, limbSpeed, quadSwing, type HeadPose } from './legsplit.ts';
import {
  axesFromMeshSize,
  deriveQuadAnatomy,
  headingTurn,
  type QuadAnatomy,
  type RigBone,
} from './quadrig.ts';
import { bindRig, type BoundRig } from './sheeprig.ts';
import { FOX_SWING, foxCarriage, poseFox } from './foxrig.ts';
import { LAMB_URL, sheepDaylight } from './sheepmesh.ts';
import { TIP_SECONDS, lyingPose } from './sheepfall.ts';
import { stamped } from './assets.ts';

/** 🦊 Where the model is served from. */
export const FOX_URL = stamped('/models/fox.glb');

/**
 * 🎨 **The four coats, as tints on a WHITE body.** ⚠️ Dials, judged on a
 * picture: a multiply can only darken, which is why the body is generated white.
 */
const COATS = {
  /** ❄️ a white fox in winter — the body's own colour */
  white: 0xffffff,
  /** ☀️ a white fox in summer — brown-grey */
  whiteSummer: 0x9a8a78,
  /** 🌑 the dark "blue" fox in winter — slate */
  darkWinter: 0x5c5f68,
  /** 🌑 the dark fox in summer — dark chocolate brown */
  darkSummer: 0x5a4a3e,
} as const;
type CoatName = keyof typeof COATS;

/** 🩸 How long the red lasts after a hit, seconds — the sheep's. */
const HURT_FLASH_SECONDS = 0.5;
/** 📏 How fast a drawn body chases where the server put it. */
const CHASE = 7;
/** 📏 How fast a pose blends in and out, per second. */
const BLEND = 3;
/** 🐭 How long the leap takes, seconds — the server holds `Pounce` for 1.3. */
const POUNCE_SECONDS = 1.2;
/** 🐑 The lamb in its jaws, drawn this long, metres. */
const JAW_LAMB_LENGTH = 0.45;

interface Kit {
  readonly template: Group;
  readonly anatomy: QuadAnatomy;
  readonly boneOrder: readonly string[];
  /** 📐 The hind feet, as metres along the body — what a sit pivots on. */
  readonly pivot: number;
  readonly materials: Readonly<Record<CoatName | 'hurt', unknown>>;
}

/** One fox as it is being drawn. */
interface Live {
  readonly id: string;
  readonly coat: 'dark' | 'white';
  tx: number;
  ty: number;
  tz: number;
  tyaw: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  doing: string | null;
  lookAt: { x: number; y: number; z: number } | null;
  walked: number;
  gait: number;
  head: HeadPose;
  sit: number;
  curl: number;
  sniff: number;
  /** 🐭 seconds into the current leap, or `null` */
  pouncing: number | null;
  fallen: number;
  hits: number;
  flash: number;
  prey: boolean;
  object: Group;
  tilt: Group;
  skin: { material: unknown } | null;
  rig: BoundRig | null;
  lamb: Object3D | null;
}

/**
 * 🦊 **Every fox the server has told us about.** The same shape as `Flock`:
 * `setFrom` takes the server's word, `update` runs every frame, `stats` goes
 * into an F8 report.
 */
export class Foxes {
  readonly group = new Group();
  private readonly live = new Map<string, Live>();
  private kit: Kit | null = null;
  private lambTemplate: Group | null = null;
  private origin = { x: 0, y: 0, z: 0 };
  private month = 6;
  private measured: Record<string, unknown> = { ready: false };

  constructor(url: string = FOX_URL, lambUrl: string = LAMB_URL) {
    void this.load(url);
    void this.loadLamb(lambUrl);
  }

  /** 🗓️ The game's month, so a white fox wears the right coat. */
  setMonth(month: number): void {
    this.month = month;
  }

  setOrigin(origin: { x: number; y: number; z: number }): void {
    this.origin = origin;
  }

  /** 🔴 Load, MEASURE, and READ THE SKELETON — once. `sheepmesh.ts` says why. */
  private async load(url: string): Promise<void> {
    try {
      const gltf = await new GLTFLoader().loadAsync(url);
      const scene = gltf.scene;
      scene.updateMatrixWorld(true);
      let skinned: Object3D | null = null;
      let map: unknown = null;
      scene.traverse((o) => {
        const any = o as {
          isSkinnedMesh?: boolean;
          isMesh?: boolean;
          material?: { map?: unknown };
        };
        if (any.isSkinnedMesh === true && skinned === null) skinned = o;
        if (any.isMesh === true && any.material?.map && map === null) map = any.material.map;
      });
      if (skinned === null) {
        console.error('[fox] no SkinnedMesh in the fox model — it is not rigged');
        this.measured = { ready: false, reason: 'no SkinnedMesh' };
        return;
      }
      const size = new Box3().setFromObject(scene).getSize(new Vector3()).toArray();
      const axes = axesFromMeshSize(size);
      const lengthNow = size[axes.length] ?? 1;
      const scale = lengthNow > 0 ? FOX_LENGTH / lengthNow : 1;
      const holder = new Group();
      holder.add(scene);
      scene.scale.setScalar(scale);
      holder.updateMatrixWorld(true);
      scene.position.y -= new Box3().setFromObject(holder).min.y;
      holder.updateMatrixWorld(true);

      const bones: Object3D[] = [];
      scene.traverse((o) => {
        if ((o as { isBone?: boolean }).isBone === true) bones.push(o);
      });
      const order = bones.map((b) => b.name);
      const at = new Vector3();
      const plain: RigBone[] = bones.map((b) => {
        b.getWorldPosition(at);
        return {
          name: b.name,
          parent: b.parent ? order.indexOf(b.parent.name) : -1,
          world: [at.x, at.y, at.z] as [number, number, number],
        };
      });
      const anatomy = deriveQuadAnatomy(plain, axes);
      const painted = markFleece(skinned, anatomy, plain);
      /* 🦊 The root bone off the paws — see `shedRootWeights`. */
      const shed = shedRootWeights(skinned);
      const coat = (hex: number) => {
        const tint = uniform(new Color(hex));
        return creatureMaterial(map, tint, tint, sheepDaylight);
      };
      const materials = {
        white: coat(COATS.white),
        whiteSummer: coat(COATS.whiteSummer),
        darkWinter: coat(COATS.darkWinter),
        darkSummer: coat(COATS.darkSummer),
        hurt: creatureMaterial(map, HURT_FLEECE, HURT_BARE, sheepDaylight),
      };
      (skinned as { material: unknown }).material = materials.white;
      (skinned as { frustumCulled: boolean }).frustumCulled = false;

      const turnToX = headingTurn(plain, anatomy);
      holder.rotation.y = turnToX;
      holder.updateMatrixWorld(true);

      /* 📐 The hind feet, after the turn: a sit tips the body about them. */
      let pivot = -FOX_LENGTH * 0.2;
      const hind = anatomy.legs.filter((l) => !l.fore).map((l) => l.bones.at(-1));
      const feet = hind.flatMap((i) => (i === undefined ? [] : [bones.at(i)]));
      if (feet.length > 0) {
        pivot = feet.reduce((s, b) => s + (b?.getWorldPosition(at).x ?? 0), 0) / feet.length;
      }

      this.kit = { template: holder, anatomy, boneOrder: order, pivot, materials };
      this.measured = {
        ready: true,
        bones: bones.length,
        scale: Number(scale.toFixed(4)),
        legs: anatomy.legs.length,
        neck: anatomy.neck.length,
        tail: anatomy.tail?.length ?? 0,
        legBones: anatomy.legs.map((l) => l.bones.length).join('/'),
        pivot: Number(pivot.toFixed(3)),
        fleeceShare: painted.fleeceShare,
        shedFromRoot: shed,
      };
    } catch (err: unknown) {
      console.error('[fox] the fox model did not load', err);
    }
  }

  /** 🐑 The lamb a fox carries — the game's own lamb body, at rest, small. */
  private async loadLamb(url: string): Promise<void> {
    try {
      const gltf = await new GLTFLoader().loadAsync(url);
      const scene = gltf.scene;
      const size = new Box3().setFromObject(scene).getSize(new Vector3()).toArray();
      const longest = Math.max(...size, 1e-6);
      const holder = new Group();
      holder.add(scene);
      scene.scale.setScalar(JAW_LAMB_LENGTH / longest);
      scene.traverse((o) => {
        if ((o as { isSkinnedMesh?: boolean }).isSkinnedMesh === true)
          (o as unknown as { frustumCulled: boolean }).frustumCulled = false;
      });
      this.lambTemplate = holder;
    } catch (err: unknown) {
      console.error('[fox] the lamb model did not load — a fox will carry nothing', err);
    }
  }

  /** Take the server's word. Only the foxes in it. */
  setFrom(message: CreaturesMessage): void {
    const seen = new Set<string>();
    for (const c of message.creatures) {
      if (c.kind !== FOX_KIND) continue;
      seen.add(c.id);
      const had = this.live.get(c.id);
      if (had === undefined) {
        const object = new Group();
        const tilt = new Group();
        object.add(tilt);
        const made: Live = {
          id: c.id,
          coat: foxCoat(c.id),
          tx: c.x,
          ty: c.y,
          tz: c.z,
          tyaw: c.yaw,
          x: c.x,
          y: c.y,
          z: c.z,
          yaw: c.yaw,
          doing: c.doing ?? null,
          lookAt: c.lookAt ?? null,
          walked: 0,
          gait: 0,
          head: { yaw: 0, pitch: 0 },
          /* A fox first seen asleep is drawn asleep, not curling up in front of you. */
          sit: c.doing === 'Watch' ? 1 : 0,
          curl: c.doing === 'Sleep' ? 1 : 0,
          sniff: 0,
          pouncing: null,
          fallen: 0,
          hits: c.hits ?? 0,
          flash: 0,
          prey: c.prey === true,
          object,
          tilt,
          skin: null,
          rig: null,
          lamb: null,
        };
        this.live.set(c.id, made);
        this.group.add(object);
      } else {
        had.tx = c.x;
        had.ty = c.y;
        had.tz = c.z;
        had.tyaw = c.yaw;
        if (c.doing === 'Pounce' && had.doing !== 'Pounce') had.pouncing = 0;
        had.doing = c.doing ?? null;
        had.lookAt = c.lookAt ?? null;
        const hits = c.hits ?? 0;
        if (hits > had.hits) had.flash = HURT_FLASH_SECONDS;
        had.hits = hits;
        had.prey = c.prey === true;
      }
    }
    for (const [id, one] of this.live)
      if (!seen.has(id)) {
        this.group.remove(one.object);
        this.live.delete(id);
      }
  }

  /** 🧍 Every fox as a box you bump into — but not one lying dead. */
  bodies(): Bulk[] {
    const out: Bulk[] = [];
    for (const one of this.live.values()) {
      if (one.doing === 'Dead') continue;
      out.push({ x: one.x, y: one.y, z: one.z, half: FOX_WIDTH / 2, height: FOX_HEIGHT });
    }
    return out;
  }

  /** Put the body on a fox that has none yet — the model may load after it arrives. */
  private dress(one: Live): void {
    const kit = this.kit;
    if (kit === null || one.rig !== null) return;
    const body = cloneSkinned(kit.template);
    body.position.x = -kit.pivot;
    one.tilt.position.x = kit.pivot;
    one.tilt.add(body);
    one.rig = bindRig(body, kit.anatomy, kit.boneOrder);
    body.traverse((node) => {
      if ((node as { isSkinnedMesh?: boolean }).isSkinnedMesh === true && one.skin === null)
        one.skin = node as unknown as { material: unknown };
    });
  }

  private coatOf(one: Live): CoatName {
    const winter = isWinterCoat(this.month);
    if (one.coat === 'white') return winter ? 'white' : 'whiteSummer';
    return winter ? 'darkWinter' : 'darkSummer';
  }

  /** 🦊 Every frame: chase the server, then pose the body. */
  update(dt: number): void {
    const chase = Math.min(1, dt * CHASE);
    const blend = Math.min(1, dt * BLEND);
    const head = new Vector3();
    for (const one of this.live.values()) {
      this.dress(one);
      const wasX = one.x;
      const wasZ = one.z;
      one.x += (one.tx - one.x) * chase;
      one.y += (one.ty - one.y) * chase;
      one.z += (one.tz - one.z) * chase;
      const delta = ((one.tyaw - one.yaw + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
      one.yaw += delta * chase;
      const step = Math.hypot(one.x - wasX, one.z - wasZ);
      one.walked += step;
      /* 🦶 Against the fox's WALK, so a walking fox swings its legs fully. ⚠️ It
         was against the trot, and a walking fox swung them half way — one of the
         three reasons haugur saw it "floating than walking". */
      one.gait = limbSpeed(one.gait, dt > 0 ? step / dt : 0, dt, FOX_SPEED);

      const toward = (v: number, want: number) => v + (want - v) * blend;
      one.sit = toward(one.sit, one.doing === 'Watch' ? 1 : 0);
      one.curl = toward(one.curl, one.doing === 'Sleep' ? 1 : 0);
      one.sniff = toward(one.sniff, one.doing === 'Sniff' ? 1 : 0);
      if (one.pouncing !== null) {
        one.pouncing += dt;
        if (one.pouncing >= POUNCE_SECONDS) one.pouncing = null;
      }

      const root = one.object;
      root.position.set(one.x - this.origin.x, one.y - this.origin.y, one.z - this.origin.z);
      root.rotation.order = 'YXZ';
      root.rotation.y = -one.yaw;
      one.fallen = one.doing === 'Dead' ? Math.min(1, one.fallen + dt / TIP_SECONDS) : 0;
      const lying = lyingPose(one.fallen);
      root.rotation.x = lying.roll;
      root.position.y += lying.lift * (FOX_WIDTH / 0.42);

      if (one.skin !== null && this.kit !== null) {
        one.flash = Math.max(0, one.flash - dt);
        const want = one.flash > 0 ? this.kit.materials.hurt : this.kit.materials[this.coatOf(one)];
        if (one.skin.material !== want) one.skin.material = want;
      }

      if (one.rig === null) continue;

      let wantYaw = 0;
      let wantPitch = 0;
      if (one.lookAt !== null && one.curl < 0.5) {
        const dx = one.lookAt.x - one.x;
        const dz = one.lookAt.z - one.z;
        const away = ((Math.atan2(dz, dx) - one.yaw + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
        wantYaw = -away;
        wantPitch =
          -Math.atan2(
            one.lookAt.y - (one.y + FOX_HEIGHT * 0.8),
            Math.max(Math.hypot(dx, dz), 0.5),
          ) * 0.6;
      }
      one.head = headTowards(one.head, wantYaw, wantPitch, dt);

      const stride = FOX_LENGTH * 0.35;
      const pose = {
        /* 🦶 A fox's legs are short, so they swing further than a sheep's 17°
           (`FOX_SWING`); the paw lift keeps time with it via the same phase. */
        swing: quadSwing(one.walked, stride, one.gait * FOX_SWING)[0] ?? 0,
        stepPhase: (Math.PI / stride) * one.walked,
        step: one.gait,
        headYaw: one.head.yaw,
        headPitch: one.head.pitch,
        /* 🦊 The brush streams and swings as it moves — 🎯 "tail moves". */
        tailWag: Math.min(1, one.gait * 1.5),
        lean: 0,
        sit: one.sit,
        curl: one.curl,
        sniff: one.sniff,
        pounce: one.pouncing === null ? 0 : one.pouncing / POUNCE_SECONDS,
      };
      poseFox(one.rig, pose);
      const carriage = foxCarriage(pose);
      one.tilt.rotation.z = carriage.tilt;
      one.tilt.position.y = carriage.lift;

      /* 🐑 A lamb in its jaws — held across the muzzle, hanging. */
      const wantLamb = one.prey && this.lambTemplate !== null;
      if (wantLamb && one.lamb === null && this.lambTemplate !== null) {
        one.lamb = cloneSkinned(this.lambTemplate);
        root.add(one.lamb);
      } else if (!wantLamb && one.lamb !== null) {
        root.remove(one.lamb);
        one.lamb = null;
      }
      const skull = one.rig.neck.at(-1)?.bone;
      if (one.lamb !== null && skull !== undefined) {
        root.updateMatrixWorld(true);
        skull.getWorldPosition(head);
        root.worldToLocal(head);
        one.lamb.position.set(head.x + 0.1, head.y - 0.22, head.z);
        one.lamb.rotation.set(0, Math.PI / 2, 0.3);
      }
    }
  }

  /** 🔬 Into an F8 report: what the model measured and what the first fox is doing. */
  get stats(): Record<string, unknown> {
    const first = this.live.values().next().value;
    return {
      ...this.measured,
      drawn: this.live.size,
      doing: first?.doing ?? null,
      coat: first ? this.coatOf(first) : null,
      bound: first?.rig?.found ?? null,
    };
  }

  dispose(): void {
    this.group.clear();
    this.live.clear();
  }
}
